if (!customElements.get('acmer-newsroom-article')) {
  customElements.define('acmer-newsroom-article', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.body = this.querySelector('[data-article-body]');
      this.toc = this.querySelector('[data-article-toc]');
      this.headers = [...document.querySelectorAll('.shopify-section-group-header-group, .hdt-main-header')];
      this.headings = [...this.body.querySelectorAll(this.dataset.subheadings === 'true' ? 'h2, h3' : 'h2')].filter(heading => heading.textContent.trim());
      if (!this.headings.length) this.headings = [...this.body.querySelectorAll('h3')].filter(heading => heading.textContent.trim());
      const ids = new Set([...document.querySelectorAll('[id]')].map(element => element.id));
      this.links = this.headings.map((heading, index) => {
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          const base = heading.textContent.trim().toLowerCase().replace(/[^\p{L}\p{N}_-]+/gu, '-').replace(/^-+|-+$/g, '') || `section-${index + 1}`;
          let id = base;
          let suffix = 2;
          while (ids.has(id)) id = `${base}-${suffix++}`;
          heading.id = id;
          ids.add(id);
        }
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(heading.id)}`;
        link.textContent = heading.textContent.trim();
        if (heading.tagName === 'H3') link.classList.add('newsroom-article__toc-subheading');
        link.addEventListener('click', event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (location.hash !== link.hash) history.pushState(null, '', link.hash);
          this.scrollToHeading(heading, !window.matchMedia('(prefers-reduced-motion: reduce)').matches);
          if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
          heading.focus({ preventScroll: true });
        }, options);
        item.append(link);
        return { item, link, heading };
      });
      this.toc.replaceChildren(...this.links.map(entry => entry.item));
      this.toc.closest('aside').hidden = this.links.length === 0;
      this.body.querySelectorAll('table').forEach(table => {
        if (table.parentElement.classList.contains('newsroom-article__table')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'newsroom-article__table';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', table.caption?.textContent || 'Scrollable article table');
        table.before(wrapper);
        wrapper.append(table);
      });
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
      };
      window.addEventListener('scroll', schedule, { ...options, passive: true });
      window.addEventListener('resize', schedule, options);
      window.addEventListener('hashchange', () => this.followHash(), options);
      this.addEventListener('load', schedule, { ...options, capture: true });
      this.resizeObserver = new ResizeObserver(schedule);
      this.resizeObserver.observe(this.body);
      this.headers.forEach(header => this.resizeObserver.observe(header));
      this.sync();
      this.followHash();
    }

    measureHeader() {
      const bottom = this.headers.reduce((maximum, header) => {
        const box = header.getBoundingClientRect();
        return box.width && box.height && box.top < innerHeight && box.bottom > 0 ? Math.max(maximum, box.bottom) : maximum;
      }, 0);
      this.offset = bottom + (Number(this.dataset.stickyOffset) || 0);
      this.style.setProperty('--article-sticky-top', `${this.offset}px`);
    }

    scrollToHeading(heading, smooth = false) {
      this.measureHeader();
      window.scrollTo({ top: Math.max(0, window.scrollY + heading.getBoundingClientRect().top - this.offset - 16), behavior: smooth ? 'smooth' : 'instant' });
      this.sync();
    }

    followHash() {
      let id;
      try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
      const heading = this.headings.find(item => item.id === id);
      if (heading) this.scrollToHeading(heading);
    }

    sync() {
      this.measureHeader();
      if (!this.links.length) return;
      let current = this.links[0];
      for (const entry of this.links) {
        if (entry.heading.getBoundingClientRect().top <= this.offset + 24) current = entry;
      }
      if (window.scrollY > 0 && window.scrollY + innerHeight >= document.documentElement.scrollHeight - 2) current = this.links.at(-1);
      if (current === this.current) return;
      this.current = current;
      this.links.forEach(entry => {
        if (entry === current) entry.link.setAttribute('aria-current', 'location');
        else entry.link.removeAttribute('aria-current');
      });
      const nav = this.toc.parentElement;
      const bounds = nav.getBoundingClientRect();
      const linkBounds = current.link.getBoundingClientRect();
      if (linkBounds.top < bounds.top) nav.scrollTop -= bounds.top - linkBounds.top + 8;
      else if (linkBounds.bottom > bounds.bottom) nav.scrollTop += linkBounds.bottom - bounds.bottom + 8;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.current = null;
    }
  });
}
